<template>
    <Modal @shown="shown" @hidden="hidden" ref="modal" modalClass="modal-md" @submit="submit">
        <template #title>
            <template v-if="trigger && !readonly">
                {{ i18n('Edit {name}').replace('{name}', i18n('Trigger')) }}
            </template>
            <template v-else-if="trigger">
                {{ i18n('{name} detail').replace('{name}', i18n('Trigger')) }}
            </template>
            <template v-else>
                {{ i18n('Add {name}').replace('{name}', i18n('Trigger')) }}
            </template>
        </template>

        <div class="alert alert-danger mb-2" v-if="errorMessage">{{ errorMessage }}</div>

        <div class="row g-2">
            <div class="col-12" :class="{ 'col-sm-6': formData.application }">
                <Field v-model="formData.application" :errorMessage="errorMessages.application"
                    :label="i18n('Limit to Application')" :disabled="submitted" rest="applications" format="object"
                    :type="readonly ? 'readonly' : 'dictionary'" />
            </div>
            <div class="col-12 col-sm-6" v-if="formData.application">
                <Field v-model="formData.profile" :errorMessage="errorMessages.profile"
                    :label="i18n('Limit to Profile')" :disabled="submitted"
                    :rest="`applications/${this.formData.application.id}/profiles`" format="object"
                    :type="readonly ? 'readonly' : 'dictionary'" />
            </div>
            <p>
                {{ triggerDescription }}
            </p>
            <div class="col-12" v-if="formData.application">
                <Field v-model="formData.triggerAction" :errorMessage="errorMessages.triggerAction"
                    :label="i18n('Action')" :disabled="submitted" :list="triggerActions" format="object"
                    :type="readonly ? 'readonly' : 'dictionary'" />
            </div>

            <div class="col-12">
                <Field v-model="formData.triggerType" :errorMessage="errorMessages.triggerType" :label="i18n('Type')"
                    :disabled="submitted" :list="triggerTypes" format="object"
                    :type="readonly ? 'readonly' : 'dictionary'" />
            </div>

            <div v-if="formData.triggerType && formData.triggerType.code == 'ad-group'">
                <TriggerTypeAdGroup :formData="formData" :submitted="submitted" :readonly="readonly"
                    :errorMessages="errorMessages" />
            </div>
            <div v-else-if="formData.triggerType && formData.triggerType.code == 'send-email'">
                <TriggerTypeSendEmail :formData="formData"
                    :triggerType="triggerTypes.filter(type => type.code == 'send-email')[0]" :submitted="submitted"
                    :readonly="readonly" :errorMessages="errorMessages" />
            </div>
            <div v-else-if="formData.triggerType && formData.triggerType.code == 'send-to-service-now'">
                <TriggerTypeSendToServiceNow :formData="formData"
                    :triggerType="triggerTypes.filter(type => type.code == 'send-to-service-now')[0]"
                    :submitted="submitted" :readonly="readonly" :errorMessages="errorMessages" />
            </div>
        </div>

        <template #footer>
            <div class="row">
                <template v-if="!readonly">
                    <div class="col text-start">
                        <button type="button" class="btn btn-danger" @click="hide">
                            {{ i18n('Cancel') }}
                        </button>
                    </div>
                    <div class="col text-end">
                        <button type="submit" class="btn btn-primary text-white" :disabled="submitted">
                            {{ i18n('Confirm') }}
                        </button>
                    </div>
                </template>
                <div class="col text-end" v-else>
                    <button type="button" @click="hide" class="btn btn-outline-dark btn-sm me-2">
                        {{ i18n('Close') }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script>
import api from "@/api.js";
import { duplicate, } from "@/tools/object.js";
import { translatedErrorMessage, } from "@/tools/errorHandling.js";
import Modal from "@/components/common/layout/Modal.vue";
import Field from "@/components/common/form/Field.vue";
import TriggerTypeAdGroup from "@/components/trigger/triggerType/TriggerTypeAdGroup.vue"
import TriggerTypeSendEmail from "@/components/trigger/triggerType/TriggerTypeSendEmail.vue"
import TriggerTypeSendToServiceNow from "@/components/trigger/triggerType/TriggerTypeSendToServiceNow.vue"

export default {
    components: {
        Modal,
        Field,
        TriggerTypeAdGroup,
        TriggerTypeSendEmail,
        TriggerTypeSendToServiceNow,
    },
    props: {},
    data() {
        return {
            triggerId: null,
            trigger: null,
            submitted: false,
            formData: {},
            errorMessages: {},
            errorMessage: '',
            triggerTypes: [],
            triggerActions: [],
        };
    },
    emits: [
        'shown',
        'hidden',
        'created',
        'updated',
    ],
    computed: {
        readonly() {
            if (!this.trigger) {
                return false;
            }

            return this.trigger.acl?.update !== true;
        },
        triggerDescription() {
            let message = '';

            if (!this.formData.application) {
                message = 'The trigger will fire when an user is created.';
            } else if (!this.formData.profile) {
                message = 'The trigger will fire when an user\'s profiles in the application {{application}} or its username are changed.';
            } else {
                message = 'The trigger will fire when, in the application {{application}}, the profile {{profile}} is added to or removed from a user.';
            }

            return this.i18n(message, {
                application: this.formData?.application?.name,
                profile: this.formData?.profile?.name,
            });
        },
    },
    methods: {
        show(triggerId) {
            api.get('types/triggers').then(triggerTypes => {
                this.triggerTypes = triggerTypes;
            });

            api.get('types/triggerActions').then(triggerActions => {
                this.triggerActions = triggerActions;
            });

            this.triggerId = triggerId;
            this.trigger = null;
            this.load().then(() => {
                this.submitted = false;
                this.$refs['modal'].show();
                this.errorMessages = {};
                this.errorMessage = '';

                if (this.trigger) {
                    this.formData = duplicate(this.trigger);
                } else {
                    this.formData = {};
                }

                if (!this.formData.params || Array.isArray(this.formData.params)) {
                    this.formData.params = {};
                }
            });
        },
        load() {
            if (!this.triggerId) {
                return Promise.resolve();
            }

            let promise = api.get(`triggers/${this.triggerId}`, { acl: 'trigger' });

            promise.then(trigger => {
                this.trigger = trigger;
            });

            return promise;
        },
        hide() {
            this.$refs['modal'].hide();
        },
        shown() {
            this.$emit('shown');
        },
        hidden() {
            this.$emit('hidden');
        },
        submit() {
            this.submitted = true;
            this.errorMessages = {};
            this.errorMessage = '';

            if (!this.formData.application) {
                this.formData.triggerAction = this.triggerActions.filter(action => action.code === 'create')[0];
            }

            const postFormData = duplicate(this.formData);
            let savePromise;

            if (!this.trigger) {
                savePromise = api.post(`triggers`, postFormData);
            } else {
                savePromise = api.put(`triggers/${this.trigger.id}`, postFormData);
            }

            savePromise.then(trigger => {
                if (!this.trigger) {
                    this.$emit('created', trigger);
                } else {
                    this.$emit('updated', trigger);
                }

                this.hide();
            }).catch((errorMessage) => {
                this.errorMessage = translatedErrorMessage(errorMessage);
                this.submitted = false;
            });
        }
    }
}
</script>